import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFileDownload,
  FaPrint,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";

import { formatFieldAppDateTime } from "./fieldAppTimezoneDisplay";
import FieldAppScheduleWhen from "./FieldAppScheduleWhen";
import { buildMachineOperationMap } from "./fieldAppMachineUtils";
import FieldAppTelemetryMapPanel from "./FieldAppTelemetryMapPanel";
import {
  formatHoursDotMinutes,
  sortTelemetryScheduleRows,
} from "./fieldAppTelemetryScheduleFormat";
import {
  buildTelemetryScheduleFileName,
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";

import "./FieldAppTelemetrySchedulePanel.css";
import "./FieldAppTelemetryMapPanel.css";

function formatHours(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) {
    return "—";
  }
  return `${hours.toFixed(2)} h`;
}

function formatSpeed(value) {
  const speed = Number(value);
  if (!Number.isFinite(speed) || speed <= 0) {
    return "—";
  }
  return `${speed.toFixed(1)} km/h`;
}

function formatStoppedPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent < 0) {
    return "—";
  }
  return `${percent.toFixed(1)}%`;
}

function formatDayLabel(dateIso, compact) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  if (compact) {
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

function formatReportGeneratedAt() {
  return new Date().toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function openMapView(setMapView, payload) {
  setMapView({
    ...payload,
    token: Date.now(),
  });
}

function mapCenterFromPoint(point) {
  if (!point) return null;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, time: point.iso || point.time || point.focusStart || null };
}

function ScheduleTimeCell({
  cell,
  dayKey,
  label,
  timezoneMode,
  onOpenMap,
  operatorIds,
  t,
  printable,
}) {
  if (!cell || !cell.iso) {
    return <span className="field-app-telemetry-schedule__empty">—</span>;
  }

  const stamp = (
    <FieldAppScheduleWhen iso={cell.iso} timezoneMode={timezoneMode} />
  );
  const canOpen = !printable && cell.focusStart && cell.focusEnd && onOpenMap;

  if (!canOpen) {
    return stamp;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={formatFieldAppDateTime(cell.iso, timezoneMode, "card")}
      onClick={() =>
        onOpenMap({
          dayKey: dayKey || String(cell.iso).slice(0, 10),
          focusStart: cell.focusStart,
          focusEnd: cell.focusEnd,
          label,
          mapCenter: mapCenterFromPoint(cell),
          mapFullDay: true,
          operatorIds:
            operatorIds && operatorIds.length
              ? operatorIds
              : cell.operatorIds,
        })
      }
    >
      {stamp}
    </button>
  );
}

function MaxSpeedCell({ row, onOpenMap, t, printable }) {
  const point = row.maxSpeedAt;
  const label = formatSpeed(row.maxSpeedKmh);
  if (!point || !point.focusStart || !point.focusEnd || !onOpenMap || printable) {
    return <span>{label}</span>;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={t("Abrir mapa no ponto de velocidade máxima")}
      onClick={() =>
        onOpenMap({
          dayKey: row.date,
          focusStart: point.focusStart,
          focusEnd: point.focusEnd,
          label: t("Velocidade máxima ({{speed}})", { speed: label }),
          mapCenter: mapCenterFromPoint(point),
          mapFullDay: true,
          operatorIds:
            row.operatorId != null ? [row.operatorId] : undefined,
        })
      }
    >
      {label}
    </button>
  );
}

function buildOperationReturnSearch(farmId, dayKey) {
  const params = new URLSearchParams({
    return_to: `/${farmId}/field-app/?tab=telemetry`,
  });
  if (dayKey) {
    params.set("telemetry_day", dayKey);
  }
  return `?${params.toString()}`;
}

function DayMachinesCell({
  machines,
  operationMap,
  farmId,
  dayKey,
  printable,
  t,
}) {
  const list = Array.isArray(machines) ? machines.filter(Boolean) : [];
  if (!list.length) {
    return <span className="field-app-telemetry-schedule__empty">—</span>;
  }

  if (printable) {
    return <span>{list.join(", ")}</span>;
  }

  return (
    <div className="field-app-telemetry-schedule__machines">
      {list.map((ident, index) => {
        const meta = operationMap[String(ident).trim()] || {};
        const operationId = meta.monitoringOperationId;
        const key = `${dayKey}-${ident}-${index}`;
        if (!operationId) {
          return (
            <span
              key={key}
              className="field-app-telemetry-schedule__machine-label"
            >
              {ident}
            </span>
          );
        }
        return (
          <Link
            key={key}
            className="field-app-telemetry-schedule__machine-link"
            to={`/${farmId}/operation/${operationId}${buildOperationReturnSearch(
              farmId,
              dayKey
            )}`}
            title={t("Abrir mapa e operações desta máquina")}
          >
            {ident}
          </Link>
        );
      })}
    </div>
  );
}

const TELEMETRY_SCHEDULE_COLUMNS = [
  { key: "day", label: (t) => t("Dia") },
  { key: "operator", label: (t) => t("Operador") },
  { key: "start", label: (t) => t("Início") },
  { key: "pause", label: (t) => t("Pausa") },
  { key: "resume", label: (t) => t("Retorno") },
  {
    key: "finish",
    label: (t) => t("Encerramento"),
    compact: (t) => t("Encerr."),
  },
  { key: "morning", label: (t) => t("Manhã") },
  { key: "afternoon", label: (t) => t("Tarde") },
  { key: "total", label: (t) => t("Total") },
  { key: "stopped", label: (t) => t("Parado") },
  {
    key: "motion",
    label: (t) => t("Em movimento"),
    compact: (t) => t("Em mov."),
  },
  {
    key: "stoppedPercent",
    label: (t) => t("% parado"),
    compact: (t) => "%",
  },
  {
    key: "avgSpeed",
    label: (t) => t("Vel. média"),
    compact: (t) => t("V. méd."),
  },
  {
    key: "maxSpeed",
    label: (t) => t("Vel. máxima"),
    compact: (t) => t("V. máx."),
  },
  { key: "machines", label: (t) => t("Máquinas") },
];

function cellHighlightClass(highlightedColumn, columnIndex) {
  return highlightedColumn === columnIndex
    ? "field-app-telemetry-schedule__col-highlight"
    : undefined;
}

function TelemetryScheduleTable({
  rows,
  timezoneMode,
  onOpenMap,
  operationMap,
  farmId,
  t,
  printable,
}) {
  const [highlightedColumn, setHighlightedColumn] = useState(null);

  const toggleColumn = (columnIndex) => {
    if (printable) return;
    setHighlightedColumn((current) =>
      current === columnIndex ? null : columnIndex
    );
  };

  return (
    <table
      className={`field-app-telemetry-schedule__table${
        highlightedColumn != null
          ? ` is-col-highlighted is-col-highlighted--${highlightedColumn}`
          : ""
      }`}
    >
      <thead>
        <tr>
          {TELEMETRY_SCHEDULE_COLUMNS.map((column, columnIndex) => {
            const active = highlightedColumn === columnIndex;
            const label = column.label(t);
            const compact = column.compact ? column.compact(t) : null;
            const content = compact ? (
              <>
                <span className="field-app-telemetry-schedule__screen-label">
                  {label}
                </span>
                <span className="field-app-telemetry-schedule__compact-label">
                  {compact}
                </span>
              </>
            ) : (
              label
            );

            if (printable) {
              return <th key={column.key}>{content}</th>;
            }

            return (
              <th
                key={column.key}
                className={`field-app-telemetry-schedule__th-sortable${
                  active ? " is-active" : ""
                }`}
              >
                <button
                  type="button"
                  className="field-app-telemetry-schedule__th-btn"
                  onClick={() => toggleColumn(columnIndex)}
                  aria-pressed={active}
                  title={t("Destacar esta coluna")}
                >
                  {content}
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const hasData =
            row.startedAt ||
            row.pausedAt ||
            row.resumedAt ||
            row.finishedAt ||
            (Number(row.totalHours) || 0) > 0;

          const rowKey = `${row.date}-${row.operatorId != null ? row.operatorId : "none"}`;
          const operatorIds =
            row.operatorId != null ? [row.operatorId] : undefined;

          if (!hasData) {
            return (
              <tr key={rowKey} className="field-app-telemetry-schedule__empty-row">
                <td
                  className={`field-app-telemetry-schedule__date ${
                    cellHighlightClass(highlightedColumn, 0) || ""
                  }`.trim()}
                >
                  {formatDayLabel(row.date, printable)}
                </td>
                <td colSpan={14}>{t("Sem telemetria neste dia")}</td>
              </tr>
            );
          }

          const splitDay = (Number(row.afternoonHours) || 0) > 0;
          const hl = (columnIndex) =>
            cellHighlightClass(highlightedColumn, columnIndex);

          return (
            <tr key={rowKey}>
              <td
                className={`field-app-telemetry-schedule__date ${
                  hl(0) || ""
                }`.trim()}
              >
                {formatDayLabel(row.date, printable)}
              </td>
              <td className={hl(1)}>
                {row.operatorName || "—"}
              </td>
              <td className={hl(2)}>
                <ScheduleTimeCell
                  cell={row.startedAt}
                  dayKey={row.date}
                  label={t("Início")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  operatorIds={operatorIds}
                  printable={printable}
                  t={t}
                />
              </td>
              <td className={hl(3)}>
                <ScheduleTimeCell
                  cell={row.pausedAt}
                  dayKey={row.date}
                  label={t("Pausa")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  operatorIds={operatorIds}
                  printable={printable}
                  t={t}
                />
              </td>
              <td className={hl(4)}>
                <ScheduleTimeCell
                  cell={row.resumedAt}
                  dayKey={row.date}
                  label={t("Retorno")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  operatorIds={operatorIds}
                  printable={printable}
                  t={t}
                />
              </td>
              <td className={hl(5)}>
                <ScheduleTimeCell
                  cell={row.finishedAt}
                  dayKey={row.date}
                  label={t("Encerramento")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  operatorIds={operatorIds}
                  printable={printable}
                  t={t}
                />
              </td>
              <td
                className={`field-app-telemetry-schedule__totals ${
                  hl(6) || ""
                }`.trim()}
              >
                {splitDay ? formatHours(row.morningHours) : formatHours(row.totalHours)}
              </td>
              <td
                className={`field-app-telemetry-schedule__totals ${
                  hl(7) || ""
                }`.trim()}
              >
                {splitDay ? formatHours(row.afternoonHours) : "—"}
              </td>
              <td
                className={`field-app-telemetry-schedule__totals ${
                  hl(8) || ""
                }`.trim()}
              >
                {formatHours(row.totalHours)}
              </td>
              <td className={hl(9)}>{formatHoursDotMinutes(row.stoppedMinutes)}</td>
              <td className={hl(10)}>{formatHoursDotMinutes(row.inMotionMinutes)}</td>
              <td className={hl(11)}>{formatStoppedPercent(row.stoppedPercent)}</td>
              <td className={hl(12)}>{formatSpeed(row.avgSpeedKmh)}</td>
              <td className={hl(13)}>
                <MaxSpeedCell
                  row={row}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td className={hl(14)}>
                <DayMachinesCell
                  machines={row.machines}
                  operationMap={operationMap}
                  farmId={farmId}
                  dayKey={row.date}
                  printable={printable}
                  t={t}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FieldAppTelemetrySchedulePanel({
  rows,
  loading,
  timezoneMode,
  farmId,
  selection,
  machines,
  operatorLabel,
  periodLabel,
  machineLabel,
  t,
}) {
  const [mapView, setMapView] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [newestFirst, setNewestFirst] = useState(true);
  const printAreaRef = useRef(null);
  const list = Array.isArray(rows) ? rows : [];
  const visibleRows = useMemo(
    () => sortTelemetryScheduleRows(list, newestFirst),
    [list, newestFirst]
  );
  const operationMap = useMemo(
    () => buildMachineOperationMap(machines),
    [machines]
  );

  const handleOpenMap = useCallback(
    (payload) => openMapView(setMapView, payload),
    []
  );

  const handlePrint = useCallback(() => {
    printTelemetrySchedule();
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!printAreaRef.current || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        buildTelemetryScheduleFileName({ operatorLabel, periodLabel })
      );
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, operatorLabel, periodLabel]);

  if (loading) {
    return (
      <div className="field-app-telemetry-schedule">
        <p className="field-app-telemetry-schedule__hint">{t("Carregando planilha…")}</p>
      </div>
    );
  }

  if (!list.length) {
    return (
      <div className="field-app-telemetry-schedule">
        <p className="field-app-telemetry-schedule__hint">
          {t("Nenhum registro de telemetria no período selecionado.")}
        </p>
      </div>
    );
  }

  return (
    <div className="field-app-telemetry-schedule">
      <div className="field-app-telemetry-schedule__toolbar">
        <div className="field-app-telemetry-schedule__toolbar-group">
          <button
            type="button"
            className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
            onClick={handlePrint}
            aria-label={t("Imprimir planilha em A4 paisagem")}
            title={t("Imprimir planilha em A4 paisagem")}
          >
            <FaPrint aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            aria-label={t("Salvar planilha em PDF (A4 paisagem)")}
            title={
              exportingPdf
                ? t("Gerando PDF…")
                : t("Salvar planilha em PDF (A4 paisagem)")
            }
          >
            <FaFileDownload aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
            onClick={() => setNewestFirst((current) => !current)}
            aria-label={
              newestFirst
                ? t("Última data em cima. Clique para inverter.")
                : t("Primeira data em cima. Clique para inverter.")
            }
            title={
              newestFirst
                ? t("Última data em cima. Clique para inverter.")
                : t("Primeira data em cima. Clique para inverter.")
            }
          >
            {newestFirst ? (
              <FaSortAmountDown aria-hidden="true" />
            ) : (
              <FaSortAmountUp aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <p className="field-app-telemetry-schedule__hint field-app-telemetry-schedule__screen-only">
        {t(
          "Cada linha é um funcionário em um dia — com vários operadores selecionados, os dados não se misturam. Clique no título de uma coluna para destacá-la. Clique em um horário ou na velocidade máxima para abrir o mapa flutuante daquele funcionário. Clique em uma máquina para abrir o mapa de operações. Paradas contam a partir de 5 minutos parado. Parado e em movimento aparecem em horas.minutos (7.21 h = 7 h e 21 min)."
        )}
      </p>

      <div
        ref={printAreaRef}
        className="field-app-telemetry-schedule__print-area"
      >
        <div className="field-app-telemetry-schedule__print-header">
          <h2 className="field-app-telemetry-schedule__print-title">
            {t("Planilha de telemetria")}
          </h2>
          <div className="field-app-telemetry-schedule__print-meta">
            {operatorLabel ? (
              <span>
                <strong>{t("Operador")}:</strong> {operatorLabel}
              </span>
            ) : null}
            {periodLabel ? (
              <span>
                <strong>{t("Período")}:</strong> {periodLabel}
              </span>
            ) : null}
            {machineLabel ? (
              <span>
                <strong>{t("Máquina")}:</strong> {machineLabel}
              </span>
            ) : null}
            <span>
              <strong>{t("Gerado em")}:</strong> {formatReportGeneratedAt()}
            </span>
          </div>
        </div>

        <div className="field-app-telemetry-schedule__wrap">
          <TelemetryScheduleTable
            rows={visibleRows}
            timezoneMode={timezoneMode}
            onOpenMap={handleOpenMap}
            operationMap={operationMap}
            farmId={farmId}
            t={t}
            printable={false}
          />
        </div>
      </div>

      {mapView ? (
        <FieldAppTelemetryMapPanel
          farmId={farmId}
          selection={selection}
          view={mapView}
          onClose={() => setMapView(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}

export default FieldAppTelemetrySchedulePanel;
